import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, Observable } from 'rxjs';
import { CaseManagementService } from '../case-management.service';

type Kind = 'party' | 'note' | 'task' | 'event' | 'cost';

/**
 * One inline "add" form for a case child collection (party / note / task / event / cost),
 * chosen by the {@link kind} input. Standalone so it drops into each detail-tab toolbar without
 * touching the existing tables/cards/SCSS. Emits {@link added} so the parent reloads that tab.
 */
@Component({
  selector: 'sp-case-add',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <button class="btn btn-secondary" (click)="toggle()">
      <span class="material-icons">{{ open ? 'close' : 'add' }}</span> {{ open ? 'Cancel' : buttonLabel }}
    </button>

    <div class="ca-form" *ngIf="open">
      <div *ngIf="err" class="ca-err">{{ err }}</div>

      <!-- PARTY -->
      <ng-container *ngIf="kind === 'party'">
        <div class="ca-grid">
          <label class="span2">Name *<input [(ngModel)]="f.name" placeholder="Person or company"></label>
          <label>Role<select [(ngModel)]="f.role">
            <option *ngFor="let o of partyRoles" [value]="o.value">{{ o.label }}</option></select></label>
          <label>Type<select [(ngModel)]="f.type">
            <option *ngFor="let o of partyTypes" [value]="o">{{ o }}</option></select></label>
          <label>ID / PIN / Reg<input [(ngModel)]="f.idNumber"></label>
          <label>Contact<input [(ngModel)]="f.contact"></label>
          <label>Email<input [(ngModel)]="f.email" type="email"></label>
          <label>Represented by<input [(ngModel)]="f.representedBy" placeholder="Counsel"></label>
          <label class="span2">Address<input [(ngModel)]="f.address"></label>
          <label class="span2">Notes<input [(ngModel)]="f.notes"></label>
        </div>
      </ng-container>

      <!-- NOTE -->
      <ng-container *ngIf="kind === 'note'">
        <label class="block">Note *<textarea [(ngModel)]="f.content" rows="3" placeholder="Write a note…"></textarea></label>
        <label class="chk"><input type="checkbox" [(ngModel)]="f.isPrivileged"> Attorney-client privileged</label>
      </ng-container>

      <!-- TASK -->
      <ng-container *ngIf="kind === 'task'">
        <div class="ca-grid">
          <label class="span2">Description *<input [(ngModel)]="f.description" placeholder="What needs doing"></label>
          <label>Assignee<input [(ngModel)]="f.assignee"></label>
          <label>Due date<input type="date" [(ngModel)]="f.dueDate"></label>
          <label>Priority<select [(ngModel)]="f.priority">
            <option *ngFor="let p of priorities" [value]="p">{{ p }}</option></select></label>
        </div>
      </ng-container>

      <!-- EVENT -->
      <ng-container *ngIf="kind === 'event'">
        <div class="ca-grid">
          <label class="span2">Title *<input [(ngModel)]="f.title" placeholder="e.g. Mention, Hearing"></label>
          <label>Type<select [(ngModel)]="f.type">
            <option *ngFor="let o of eventTypes" [value]="o.value">{{ o.label }}</option></select></label>
          <label>Date &amp; time<input type="datetime-local" [(ngModel)]="f.scheduledAt"></label>
          <label>Duration (min)<input type="number" [(ngModel)]="f.durationMinutes" min="15" step="15"></label>
          <label>Location<input [(ngModel)]="f.location"></label>
          <label class="span2">Reminders (days before)<input [(ngModel)]="f.reminders" placeholder="e.g. 7,3,1"></label>
        </div>
      </ng-container>

      <!-- COST -->
      <ng-container *ngIf="kind === 'cost'">
        <div class="ca-grid">
          <label class="span2">Description<input [(ngModel)]="f.description" placeholder="e.g. Filing fee"></label>
          <label>Type<select [(ngModel)]="f.type">
            <option *ngFor="let o of costTypes" [value]="o.value">{{ o.label }}</option></select></label>
          <label>Amount (KES)<input type="number" [(ngModel)]="f.amount" placeholder="negative = recovery"></label>
          <label>Date<input type="date" [(ngModel)]="f.date"></label>
          <label>Paid to / from<input [(ngModel)]="f.paidTo"></label>
          <label class="span2">Reference (invoice/receipt)<input [(ngModel)]="f.reference"></label>
        </div>
        <small class="ca-hint">Enter a <b>negative</b> amount for a recovery received.</small>
      </ng-container>

      <div class="ca-actions">
        <button class="btn ghost" (click)="open = false">Cancel</button>
        <button class="btn btn-primary" [disabled]="busy" (click)="save()">{{ busy ? 'Saving…' : 'Save' }}</button>
      </div>
    </div>
  `,
  styles: [`
    .ca-form { width:100%; margin-top:12px; border:1px solid #cdd8e3; border-left:4px solid #1f3b57; border-radius:8px; background:#fbfdff; padding:14px 16px; }
    .ca-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px 14px; }
    .ca-grid label, .ca-form label.block { display:flex; flex-direction:column; font-size:11px; color:#6b7a8d; gap:4px; }
    .ca-grid label.span2 { grid-column:span 2; }
    .ca-form input, .ca-form select, .ca-form textarea { padding:8px 10px; border:1px solid #cdd8e3; border-radius:6px; font-size:13px; box-sizing:border-box; width:100%; }
    .ca-form label.chk { flex-direction:row; align-items:center; gap:8px; margin-top:10px; font-size:13px; color:#516072; }
    .ca-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:14px; }
    .btn { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:6px; cursor:pointer; font-size:13px; border:1px solid #cdd8e3; background:#fff; color:#1f3b57; }
    .btn .material-icons { font-size:18px; }
    .btn-primary { background:#1f3b57; color:#fff; border:none; }
    .btn.ghost { background:#fff; }
    .btn:disabled { opacity:.5; cursor:default; }
    .ca-err { color:#c0392b; font-size:12px; margin-bottom:8px; }
    .ca-hint { display:block; color:#8190a1; font-size:12px; margin-top:8px; }
  `]
})
export class CaseAddPanelComponent {
  @Input() caseRef = '';
  @Input() kind: Kind = 'note';
  @Output() added = new EventEmitter<void>();

  private readonly svc = inject(CaseManagementService);

  open = false; busy = false; err = '';
  f: any = {};

  readonly partyRoles = [
    { value: 'DEFENDANT', label: 'Defendant' }, { value: 'PLAINTIFF', label: 'Plaintiff' },
    { value: 'WITNESS', label: 'Witness' }, { value: 'COUNSEL_PLAINTIFF', label: 'Plaintiff Counsel' },
    { value: 'COUNSEL_DEFENDANT', label: 'Defendant Counsel' }, { value: 'JUDGE', label: 'Judge' },
    { value: 'EXPERT', label: 'Expert' }, { value: 'OTHER', label: 'Other' }
  ];
  readonly partyTypes = ['INDIVIDUAL', 'COMPANY', 'GOVERNMENT'];
  readonly priorities = ['HIGH', 'MEDIUM', 'LOW'];
  readonly eventTypes = [
    { value: 'HEARING', label: 'Court Hearing' }, { value: 'MENTION', label: 'Mention' },
    { value: 'FILING_DEADLINE', label: 'Filing Deadline' }, { value: 'MEDIATION', label: 'Mediation' },
    { value: 'SETTLEMENT_MEETING', label: 'Settlement Meeting' }, { value: 'WITNESS_PREP', label: 'Witness Preparation' },
    { value: 'COURT_VISIT', label: 'Court Visit' }, { value: 'PAYMENT_DUE', label: 'Payment Due' },
    { value: 'OTHER', label: 'Other Event' }
  ];
  readonly costTypes = [
    { value: 'COURT_FEE', label: 'Court Fee' }, { value: 'LEGAL_FEE', label: 'Legal Fee' },
    { value: 'EXPERT_FEE', label: 'Expert Fee' }, { value: 'DISBURSEMENT', label: 'Disbursement' },
    { value: 'SETTLEMENT_PAID', label: 'Settlement Paid' }, { value: 'RECOVERY_RECEIVED', label: 'Recovery Received' },
    { value: 'OTHER', label: 'Other' }
  ];

  get buttonLabel(): string {
    return { party: 'Add Party', note: 'Add Note', task: 'Add Task', event: 'Schedule Event', cost: 'Record Cost / Recovery' }[this.kind];
  }

  toggle(): void {
    this.open = !this.open;
    this.err = '';
    if (this.open) { this.f = this.defaults(); }
  }

  private defaults(): any {
    switch (this.kind) {
      case 'party': return { role: 'DEFENDANT', type: 'INDIVIDUAL' };
      case 'task':  return { priority: 'MEDIUM' };
      case 'event': return { type: 'MENTION', durationMinutes: 60, reminders: '7,1' };
      case 'cost':  return { type: 'LEGAL_FEE' };
      default:      return {};
    }
  }

  async save(): Promise<void> {
    this.busy = true; this.err = '';
    try {
      await firstValueFrom(this.call());
      this.open = false;
      this.added.emit();
    } catch (e: any) {
      this.err = e?.error?.message || e?.message || 'Could not save.';
    } finally {
      this.busy = false;
    }
  }

  private call(): Observable<unknown> {
    const r = this.caseRef, f = this.f;
    switch (this.kind) {
      case 'party': return this.svc.addParty(r, {
        role: f.role, type: f.type, name: f.name, idNumber: f.idNumber, contact: f.contact,
        email: f.email, address: f.address, representedBy: f.representedBy, notes: f.notes
      } as any);
      case 'note': return this.svc.addNote(r, { content: f.content, isPrivileged: !!f.isPrivileged });
      case 'task': return this.svc.addTask(r, { description: f.description, assignee: f.assignee, dueDate: f.dueDate, priority: f.priority });
      case 'event': return this.svc.addEvent(r, {
        title: f.title, type: f.type, scheduledAt: f.scheduledAt, durationMinutes: Number(f.durationMinutes) || 60,
        location: f.location, reminderDaysBefore: this.parseReminders(f.reminders)
      });
      case 'cost': return this.svc.addCost(r, {
        date: f.date, description: f.description, type: f.type, amount: Number(f.amount) || 0,
        paidTo: f.paidTo, reference: f.reference
      });
      default: throw new Error('Unknown add kind: ' + this.kind);
    }
  }

  private parseReminders(s: string): number[] {
    return (s || '').split(',').map(x => parseInt(x.trim(), 10)).filter(n => !isNaN(n));
  }
}
